import ListItemLoading from "@/components/loading/listItemLoading/listItemLoading";
import { Customer } from "@/models/response/customer";
import { CustomerGroupResponse } from "@/models/response/customerGroup";
import AddMembersSheet from "@/modules/smsLibrary/AddMembersSheet";
import BottomSheet from "@/modules/smsLibrary/BottomSheet";
import PageHeader from "@/modules/smsLibrary/PageHeader";
import { GROUPS_PATH, findGroup, useSendToGroup } from "@/modules/smsLibrary/groups";
import { customerName, faNumber, formatPhone } from "@/modules/smsLibrary/sms";
import classes from "@/modules/smsLibrary/sms.module.scss";
import customerGroupService from "@/services/customerGroup";
import useRemoveCustomerGroup from "@/services/customerGroup/hooks/useRemoveCustomerGroup";
import { Button, Center, Loader, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconDotsVertical, IconSearch, IconSend, IconTrash, IconUser, IconUserMinus, IconUserPlus, IconUsers } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import useGetCustomerGroupsMembers from "../hooks/useGetMembers";

const MENU_SHEET = "groupMenu";

function CustomerGroupsMembers() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sendToGroup = useSendToGroup();
  const [params, setParams] = useSearchParams();
  const state = useLocation().state as { group?: CustomerGroupResponse; id?: number; title?: string } | null;
  // The id lives in the address so a refresh keeps the page; router state only saves a request.
  const id = Number(params.get("id") ?? state?.group?.id ?? state?.id ?? 0);
  const [adding, setAdding] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [term, setTerm] = useState("");
  const [search] = useDebouncedValue(term.trim(), 400);

  const groupQuery = useQuery({
    queryKey: ["customerGroup", "byId", id],
    queryFn: () => findGroup((group) => group.id === id),
    enabled: id > 0,
    initialData: state?.group?.id === id ? state.group : undefined,
  });
  const group = groupQuery.data;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } = useGetCustomerGroupsMembers({
    params: { Count: 20, customerGroupId: id, Term: search },
  });
  const members = data?.pages.flatMap((page) => page.result ?? []) ?? [];
  const total = search ? group?.membersCount ?? 0 : data?.pages[0]?.count ?? group?.membersCount ?? 0;
  const { mutate: removeGroup, isLoading: removingGroup } = useRemoveCustomerGroup();

  const observer = useRef<IntersectionObserver | null>(null);
  const lastRef = useCallback(
    (node: HTMLLIElement | null) => {
      if (isFetchingNextPage) return;
      observer.current?.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) fetchNextPage();
      });
      if (node) observer.current.observe(node);
    },
    [isFetchingNextPage, fetchNextPage, hasNextPage],
  );

  const refresh = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ["customerGroup"] });
    queryClient.invalidateQueries({ queryKey: ["customerGroupMemberIds", id] });
  };

  // Removes at once and offers an undo, instead of asking first.
  const removeMember = async (customer: Customer) => {
    await customerGroupService.removeMember({ customerId: customer.id, customerGroupId: id });
    refresh();
    const notificationId = `member-removed-${customer.id}`;
    notifications.show({
      id: notificationId,
      color: "dark",
      autoClose: 6000,
      message: (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ flex: 1 }}>{customerName(customer) || formatPhone(customer.phoneNumber)} از گروه حذف شد</span>
          <Button
            variant="subtle"
            size="compact-sm"
            fw={800}
            onClick={async () => {
              notifications.hide(notificationId);
              await customerGroupService.createMember({ customerId: customer.id, customerGroupId: id });
              refresh();
            }}
          >
            برگرداندن
          </Button>
        </span>
      ),
    });
  };

  // A new group lands here with ?add=1. Opened after mount: the sheet closes itself on mount.
  useEffect(() => {
    if (params.get("add") !== "1") return;
    const next = new URLSearchParams(params);
    next.delete("add");
    setParams(next, { replace: true, state });
    setAdding(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmRemoveGroup = () =>
    removeGroup({ id }, { onSuccess: () => navigate(GROUPS_PATH, { replace: true }) });

  if (!id) {
    return (
      <div className={classes.page}>
        <PageHeader title="گروه پیامکی" back={GROUPS_PATH} />
        <div className={classes.body}>
          <div className={classes.empty}>این گروه پیدا نشد.</div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.page}>
      <PageHeader
        title={group?.name ?? state?.title ?? "گروه پیامکی"}
        back={GROUPS_PATH}
        actions={
          <button
            type="button"
            className={classes.iconButton}
            aria-label="گزینه‌های گروه"
            onClick={() => {
              setConfirming(false);
              setMenuOpen(true);
            }}
          >
            <IconDotsVertical size={22} stroke={1.8} />
          </button>
        }
      />
      <div className={classes.body}>
        <section className={classes.card} style={{ gap: 14 }}>
          <div className={classes.summaryTop}>
            <span className={classes.summaryIcon} data-empty={total === 0}>
              <IconUsers size={25} stroke={1.8} aria-hidden />
            </span>
            <div className={classes.groupText} style={{ gap: 2 }}>
              <span className={classes.bigCount}>
                {faNumber(total)} <small>عضو</small>
              </span>
              {group?.description && <span className={classes.groupDesc}>{group.description}</span>}
            </div>
          </div>
          <div className={classes.summaryActions}>
            <Button
              flex={1.3}
              h={48}
              radius={13}
              color="myColor.7"
              fw={800}
              leftSection={<IconSend size={19} stroke={1.8} />}
              disabled={!group || total === 0}
              onClick={() => group && sendToGroup({ ...group, membersCount: total })}
            >
              ارسال پیامک
            </Button>
            <Button flex={1} h={48} radius={13} variant="outline" color="myColor.7" fw={700} leftSection={<IconUserPlus size={19} stroke={1.8} />} onClick={() => setAdding(true)}>
              افزودن اعضا
            </Button>
          </div>
        </section>

        {(total > 0 || term) && (
          <TextInput
            aria-label="جستجو در اعضا"
            placeholder="جستجو در اعضا با نام یا موبایل"
            size="md"
            radius={14}
            leftSection={<IconSearch size={19} stroke={1.8} />}
            value={term}
            onChange={(event) => setTerm(event.currentTarget.value)}
          />
        )}

        {isLoading ? (
          <ListItemLoading count={4} />
        ) : members.length === 0 ? (
          <div className={classes.empty}>
            {search ? (
              "عضوی با این نام یا شماره پیدا نشد."
            ) : (
              <>
                <strong>این گروه هنوز عضوی ندارد</strong>
                با «افزودن اعضا» چند مشتری را با هم انتخاب کنید.
              </>
            )}
          </div>
        ) : (
          <ul className={classes.list}>
            {members.map((member, index) => {
              const name = customerName(member);
              return (
                <li key={member.id} className={classes.memberRow} ref={index === members.length - 1 ? lastRef : undefined}>
                  <span className={classes.avatar} data-blank={!name}>
                    {name ? name[0] : <IconUser size={20} stroke={1.8} aria-hidden />}
                  </span>
                  <span className={classes.memberText}>
                    <span className={classes.memberName} style={name ? undefined : { direction: "ltr", textAlign: "right" }}>
                      {name || formatPhone(member.phoneNumber)}
                    </span>
                    <span className={name ? classes.memberPhone : classes.groupMeta}>{name ? formatPhone(member.phoneNumber) : "بدون نام"}</span>
                  </span>
                  <button type="button" className={classes.iconButton} aria-label={`حذف ${name || member.phoneNumber} از گروه`} onClick={() => removeMember(member)}>
                    <IconUserMinus size={20} stroke={1.8} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {isFetchingNextPage && (
          <Center>
            <Loader type="dots" />
          </Center>
        )}
      </div>

      <AddMembersSheet opened={adding} onClose={() => setAdding(false)} groupId={id} groupName={group?.name ?? state?.title} onAdded={refresh} />

      <BottomSheet opened={menuOpen} onClose={() => setMenuOpen(false)} name={MENU_SHEET} title={group?.name ?? "گزینه‌های گروه"}>
        {confirming ? (
          <div className={classes.noticeText} style={{ gap: 16 }}>
            <span style={{ fontSize: 14, lineHeight: 1.9 }}>این گروه حذف شود؟ مشتریان حذف نمی‌شوند، فقط این گروه.</span>
            <div className={classes.summaryActions}>
              <Button flex={1} h={48} radius={12} color="red" loading={removingGroup} onClick={confirmRemoveGroup}>
                حذف گروه
              </Button>
              <Button flex={1} h={48} radius={12} variant="default" onClick={() => setConfirming(false)}>
                انصراف
              </Button>
            </div>
          </div>
        ) : (
          <div className={classes.sheetActions}>
            <button type="button" className={classes.sheetAction} data-danger="true" onClick={() => setConfirming(true)}>
              <IconTrash size={21} stroke={1.8} aria-hidden />
              حذف گروه
            </button>
          </div>
        )}
      </BottomSheet>
    </div>
  );
}

export default CustomerGroupsMembers;
